import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Lightbox from '../components/Lightbox';

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setLoading(true);
    setService(null);
    setGallery(null);

    // Fetch all services and find the matching one by key
    fetch('https://api.dev.auxo.dk/public/content/services')
      .then((res) => res.json())
      .then((services) => {
        const foundService = (services || []).find((s) => s.key === slug);
        if (!foundService) {
          navigate('/404', { replace: true });
          return;
        }
        setService(foundService);

        // If service has a gallery_slug, fetch that gallery
        if (foundService.gallery_slug) {
          fetch('https://api.dev.auxo.dk/public/content/galleries')
            .then((res) => res.json())
            .then((galleries) => {
              const foundGallery = (galleries || []).find((g) => g.key === foundService.gallery_slug);
              setGallery(foundGallery);
              setLoading(false);
            })
            .catch((err) => {
              console.error('Error fetching gallery:', err);
              setLoading(false);
            });
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Error fetching service:', err);
        setLoading(false);
      });
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
      </div>
    );
  }

  if (!service) return null;

  // Custom benefits/icons based on service key
  const getServiceBenefits = (key) => {
    switch (key) {
      case 'tig-svejsning':
        return [
          { icon: 'fa-solid fa-shield-halved', title: 'Rustfrit stål', desc: 'Ideel til korrosionsbestandige samlinger.' },
          { icon: 'fa-solid fa-cubes', title: 'Aluminium', desc: 'Præcis svejsning i letvægtsmaterialer.' },
          { icon: 'fa-solid fa-utensils', title: 'Fødevareindustri', desc: 'Hygiejniske svejsninger til procesanlæg.' },
          { icon: 'fa-solid fa-bullseye', title: 'Maksimal præcision', desc: 'Fine og detaljerede samlinger.' },
        ];
      case 'co2-mig-mag':
        return [
          { icon: 'fa-solid fa-gears', title: 'Maskinrammer', desc: 'Stærke og robuste samlinger til maskiner.' },
          { icon: 'fa-solid fa-building', title: 'Stålkonstruktioner', desc: 'Velegnet til tykkere og grove emner.' },
          { icon: 'fa-solid fa-industry', title: 'Høj produktion', desc: 'Hurtig svejsehastighed til seriefremstilling.' },
          { icon: 'fa-solid fa-wrench', title: 'Reparationer', desc: 'Hurtig udbedring af slidte konstruktioner.' },
        ];
      case 'industriservice':
        return [
          { icon: 'fa-solid fa-file-contract', title: 'Serviceaftaler', desc: 'Regelmæssige eftersyn og vedligehold.' },
          { icon: 'fa-solid fa-truck-medical', title: 'Akut hjælp', desc: 'Hurtig udrykning ved uforudsete driftsstop.' },
          { icon: 'fa-solid fa-screwdriver-wrench', title: 'Forebyggende vedligehold', desc: 'Minimerer risikoen for dyre nedbrud.' },
          { icon: 'fa-solid fa-circle-check', title: 'Høj driftssikkerhed', desc: 'Sikrer stabil drift af dine anlæg.' },
        ];
      case 'svejsning-i-aluminium':
        return [
          { icon: 'fa-solid fa-feather', title: 'Letvægt', desc: 'Høj styrke kombineret med lav vægt.' },
          { icon: 'fa-solid fa-water', title: 'Korrosionsbestandig', desc: 'Perfekt til udendørs og maritime miljøer.' },
          { icon: 'fa-solid fa-dumbbell', title: 'Høj styrke', desc: 'Stærke samlinger, der tåler store belastninger.' },
          { icon: 'fa-solid fa-bullseye', title: 'Præcisionsarbejde', desc: 'Udført med det rette udstyr og teknik.' },
        ];
      default:
        return [];
    }
  };

  const benefits = getServiceBenefits(service.key);

  return (
    <div className="space-y-20 pb-0">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-blue-950 pt-32 pb-24 border-b border-slate-800 min-h-[400px] lg:min-h-[480px] flex items-center">
        {/* Full-bleed Background Image */}
        <div className="absolute inset-0 z-0">
          {service.image?.[0] && (
            <img
              src={`${service.image[0]}?width=1920&quality=85&format=auto`}
              alt={service.title?.da}
              className="h-full w-full object-cover object-center lg:object-right"
            />
          )}
          {/* Dark overlay to ensure text readability */}
          <div className="absolute inset-0 bg-blue-950/80 lg:bg-transparent lg:bg-gradient-to-r lg:from-blue-950 lg:via-blue-950/80 lg:to-transparent" />
        </div>

        <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs font-bold tracking-wider text-white hover:underline"
            >
              <i className="fa-solid fa-arrow-left" />
              Tilbage til forsiden
            </Link>
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl uppercase">
              {service.title?.da}
            </h1>
            <p className="text-lg text-slate-200 leading-relaxed max-w-2xl">
              {service.intro?.da}
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to="/kontakt"
                className="bg-brand-accent px-6 py-3 text-sm font-semibold tracking-wider text-white transition-all hover:bg-brand-accent/80"
              >
                Få et tilbud
              </Link>
              <a
                href="tel:+4561167778"
                className="inline-flex items-center gap-2 border border-white bg-transparent px-6 py-3 text-sm font-semibold tracking-wider text-white transition-all hover:bg-white/10"
              >
                <i className="fa-solid fa-phone text-sky-400" />
                <span className="whitespace-nowrap">+45 61 16 77 78</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Benefits Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          {/* Left: Rich Text Description */}
          <div className="lg:col-span-7 space-y-6">
            <h2 className="text-2xl font-extrabold text-slate-900 uppercase tracking-wider">
              Om {service.title?.da}
            </h2>
            <div
              className="prose max-w-none text-slate-600 leading-relaxed space-y-6 
                prose-headings:text-slate-900 prose-headings:font-bold prose-headings:mt-8 prose-headings:mb-4
                prose-h3:text-xl prose-h4:text-lg
                prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-2
                prose-strong:text-slate-900"
              dangerouslySetInnerHTML={{ __html: service.description?.da }}
            />
          </div>

          {/* Right: Benefits & Features */}
          <div className="lg:col-span-5 space-y-8">
            {benefits.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-brand-gray p-8 space-y-6">
                <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wider border-b border-slate-200 pb-4">
                  Særlige fordele & anvendelser
                </h3>
                <div className="space-y-6">
                  {benefits.map((benefit, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center text-black text-lg">
                        <i className={benefit.icon} />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 mb-1">{benefit.title}</h4>
                        <p className="text-xs text-slate-500 leading-relaxed">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Contact Box */}
            <div className="rounded-2xl border border-slate-200 bg-brand-gray p-8 text-center space-y-4">
              <div className="flex h-12 w-12 items-center justify-center text-black text-xl mx-auto">
                <i className="fa-solid fa-comments" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Har du spørgsmål?</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Vi står altid klar til at rådgive dig om dit projekt. Ring eller skriv til os for en uforpligtende snak.
              </p>
              <div className="pt-2 space-y-3">
                <a
                  href="tel:+4561167778"
                  className="block w-full rounded-md bg-brand-accent py-3 text-sm font-semibold tracking-wider text-white transition-colors hover:bg-brand-accent/80"
                >
                  Ring til os nu
                </a>
                <Link
                  to="/kontakt"
                  className="block w-full rounded-md border border-slate-200 bg-white py-3 text-sm font-semibold tracking-wider text-slate-800 transition-colors hover:bg-slate-50"
                >
                  Send en besked
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {gallery && gallery.images && gallery.images.length > 0 && (
        <section className="bg-brand-gray py-16 border-y border-slate-100">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-10">
            <div className="text-center max-w-2xl mx-auto space-y-3">
              <h2 className="text-3xl font-bold text-slate-900">Se vores arbejde</h2>
              <p className="text-sm text-slate-500">
                Her kan du se eksempler på udført arbejde inden for {service.title?.da?.toLowerCase()}. Klik på et billede for at se det i fuld størrelse.
              </p>
            </div>

            <div className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent">
              {gallery.images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative flex-none w-72 sm:w-80 md:w-96 aspect-square overflow-hidden border border-slate-200 bg-white cursor-pointer snap-start"
                >
                  <img
                    src={`${img}?width=600&height=600&fit=cover&quality=80&format=auto`}
                    alt={`${service.title?.da} galleri ${idx + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && gallery && (
        <Lightbox
          images={gallery.images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(index) => setLightboxIndex(index)}
        />
      )}
    </div>
  );
}
